import React from 'react';
import { BookOpen, BookMarked } from 'lucide-react';
import { NewBadge } from './CatalogNews';
import { BookInfoButton } from './FavoriteHeart';

const coverSrc = (url, backend) => (url.startsWith('http') ? url : `${backend}${url}`);
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 };
const twoLines = { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.3, height: '2.6em', wordBreak: 'break-word', margin: 0 };

const Cover = ({ book, color, backend, onRead, style }) => (
  <div onClick={onRead} data-testid={`book-cover-${book.id}`} style={{
    position: 'relative', background: `linear-gradient(135deg, ${color} 0%, ${color}99 100%)`, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', ...style
  }}>
    {book.capa_url
      ? <img src={coverSrc(book.capa_url, backend)} alt={book.titulo} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      : <BookOpen className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.7)' }} />}
  </div>
);

// Covers-only mode: just the cover and two buttons (Ler / Info)
export const CoverOnlyCard = ({ book, color, accent, backend, onRead, onDetails, maxWidth }) => (
  <div data-testid={`book-card-${book.id}`} style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', maxWidth, margin: '0 auto', minWidth: 0 }}>
    <Cover book={book} color={color} backend={backend} onRead={onRead} style={{ width: '100%', aspectRatio: '190 / 300', borderRadius: 6, boxShadow: '0 2px 8px rgba(15,23,42,0.12)' }} />
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
      <button onClick={onRead} data-testid={`read-btn-${book.id}`} style={{ height: 32, border: 'none', borderRadius: 6, background: color, color: 'white', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>Ler</button>
      <button onClick={onDetails} data-testid={`info-btn-${book.id}`} style={{ height: 32, border: 'none', borderRadius: 6, background: accent, color: 'white', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>Info</button>
    </div>
  </div>
);

export const MobileBookCard = ({ book, color, backend, isNew, onRead, onDetails, compact, heart, authors, stars }) => (
  <div data-testid={`book-card-${book.id}`} style={{
    position: 'relative', background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, borderTopLeftRadius: 0, overflow: 'hidden',
    display: 'flex', flexDirection: 'column', minWidth: 0
  }}>
    {isNew && <NewBadge color={color} />}
    <Cover book={book} color={color} backend={backend} onRead={onRead} style={{ width: '100%', aspectRatio: '380 / 550' }} />
    <div onClick={onDetails} data-testid={`book-info-${book.id}`} style={{ position: 'relative', padding: compact ? '0.4rem 0.45rem' : '0.55rem 0.6rem', minWidth: 0, cursor: 'pointer', flex: 1 }}>
      <p title={book.titulo} data-testid={`book-title-${book.id}`} style={{ ...twoLines, fontWeight: 600, color: '#2D3748', fontSize: compact ? '0.72rem' : '0.82rem' }}>{book.titulo}</p>
      <p style={{ ...ellipsis, color: '#718096', fontSize: compact ? '0.65rem' : '0.72rem' }}>{authors || book.autor || '-'}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 2, minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{stars}</div>
        <BookInfoButton bookId={book.id} onOpen={onDetails} size={compact ? 14 : 16} color={color} />
        {heart}
      </div>
    </div>
    <button onClick={onRead} data-testid={`read-btn-${book.id}`} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', width: '100%', minHeight: compact ? 34 : 38,
      border: 'none', background: color, color: 'white', fontWeight: 600, fontSize: compact ? '0.7rem' : '0.8rem', cursor: 'pointer'
    }}>
      <BookMarked className="w-3.5 h-3.5" /> Ler Agora
    </button>
  </div>
);

export const MobileListRow = ({ book, color, backend, isNew, onRead, onDetails, heart, authors, stars }) => (
  <div data-testid={`book-card-${book.id}`} style={{
    position: 'relative', display: 'flex', alignItems: 'center', gap: '0.65rem', background: 'white', border: '1px solid #e2e8f0',
    borderRadius: 10, padding: '0.5rem', minWidth: 0
  }}>
    <Cover book={book} color={color} backend={backend} onRead={onRead} style={{ width: 44, minWidth: 44, height: 64, borderRadius: 4 }} />
    <div onClick={onDetails} data-testid={`book-info-${book.id}`} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
      <p style={{ ...ellipsis, fontWeight: 600, color: '#2D3748', fontSize: '0.85rem' }}>
        {isNew && <span style={{ color, fontSize: '0.65rem', fontWeight: 700, marginRight: 6 }}>NOVO</span>}{book.titulo}
      </p>
      <p style={{ ...ellipsis, color: '#718096', fontSize: '0.75rem' }}>{authors || book.autor || '-'}</p>
      {stars}
    </div>
    {heart}
    <button onClick={onRead} data-testid={`read-btn-${book.id}`} title="Ler Agora" style={{
      width: 40, height: 40, flexShrink: 0, borderRadius: 8, border: 'none', background: color, color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
    }}>
      <BookMarked className="w-4 h-4" />
    </button>
  </div>
);
